import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { FillBlankScene } from '@/types/gameSpec';
import { normalize, SCENE_LABEL, type SceneComponentProps } from './sceneCommon';

export const FillBlankSceneView = ({
  scene,
  onAnswer,
}: SceneComponentProps<FillBlankScene>) => {
  const [value, setValue] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const submit = () => {
    if (revealed || !value.trim()) return;
    const v = normalize(value, scene.case_sensitive);
    const ok = scene.accepted_answers.some(
      (a) => normalize(a, scene.case_sensitive) === v,
    );
    setIsCorrect(ok);
    setRevealed(true);
    setTimeout(() => onAnswer(ok, { answer: value }), 800);
  };

  const [before, after] = scene.template.split('___');

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {SCENE_LABEL.fill_blank}
        </div>
        <p className="text-lg font-medium">{scene.prompt}</p>

        <div className="flex flex-wrap items-center gap-2 text-lg">
          <span>{before}</span>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            disabled={revealed}
            className="w-40 inline-block"
            autoFocus
          />
          <span>{after}</span>
        </div>

        {!revealed && (
          <Button onClick={submit} disabled={!value.trim()}>
            Check
          </Button>
        )}
        {revealed && (
          <p
            className={
              isCorrect ? 'text-green-700 font-medium' : 'text-destructive font-medium'
            }
          >
            {isCorrect
              ? 'Correct!'
              : `Answer: ${scene.accepted_answers[0]}`}
          </p>
        )}
        {scene.hint && !revealed && (
          <p className="text-sm text-muted-foreground">💡 {scene.hint}</p>
        )}
      </CardContent>
    </Card>
  );
};
